/**
 * S-DASH presentation rules (UX-RW-03 v1.1 S-DASH) as pure functions.
 *
 * Presentation only: no count is computed here. Every number shown is a
 * server value passed straight through (ADR-000 F2; RW-01 R3/R4 — no client
 * arithmetic, no client sums).
 */

import { failedListPath, pendingListPath } from './tpinPaths'
import type {
  BranchesView,
  FreshnessState,
  SummaryView,
  TpinBranch,
  TpinPeriodSummary,
  TpinScope,
} from './tpin'

/** 503 PartialResult: the body has no totals and no branch counts, so none are claimed. */
export const PARTIAL_RESULT_TEXT =
  'Partial result — some branches did not answer before the deadline. No totals are shown.'

/** Source timestamp absent: honest absence, never a page-load time (FR-6). */
export const NO_TIMESTAMP_TEXT = 'No data timestamp — no branches reporting'

export function branchCountWord(total: number): string {
  return `${total} ${total === 1 ? 'branch' : 'branches'}`
}

/** The coverage line directly under the KPI trio (D-16/R-4). */
export function coverageText(summary: Pick<TpinPeriodSummary, 'branchesTotal' | 'branchesReachable'>): string {
  if (summary.branchesTotal === 0) return 'No branches registered for this TPIN'
  if (summary.branchesReachable === 0) return 'No branches reporting'
  return `${summary.branchesReachable} of ${summary.branchesTotal} branches reporting`
}

/** Section title: `Branches (N)` where N is the server's branches_total. */
export function branchesHeading(total: number | null): string {
  return total === null ? 'Branches' : `Branches (${total})`
}

/** Position honesty under the list; `hasMore === false` is the only end signal. */
export function positionText(loaded: number, total: number | null, hasMore: boolean): string {
  if (!hasMore) return total === null ? 'All branches shown' : `All ${total} branches shown`
  return total === null ? `Showing ${loaded} branches` : `Showing ${loaded} of ${total} branches`
}

/** `branches_total` as the server reported it; unknown until the summary is complete. */
export function totalOf(view: SummaryView): number | null {
  return view.kind === 'ready' ? view.summary.branchesTotal : null
}

/**
 * The single-branch shortcut (D-13/R-2): offered only when the server says
 * there is exactly one branch AND its row has loaded AND it answered. The
 * landing is always the overview; this is a link, never a redirect.
 */
export function singleBranchOf(summary: SummaryView, branches: BranchesView): string | null {
  if (summary.kind !== 'ready' || summary.summary.branchesTotal !== 1) return null
  if (branches.kind !== 'ready') return null
  const row: TpinBranch | undefined = branches.list.rows[0]
  return row !== undefined && row.kind === 'reachable' ? row.dbName : null
}

/** What the context bar shows for freshness, from the summary view alone. */
export function freshnessOf(view: SummaryView): FreshnessState {
  if (view.kind !== 'ready') return { kind: 'pending' }
  return view.summary.freshness === null ? { kind: 'absent' } : { kind: 'stamp', raw: view.summary.freshness }
}

/** Only data-bearing views carry the marker; a view without one is not re-reading anything. */
function refreshingOf(view: SummaryView | BranchesView): boolean {
  return 'refreshing' in view && view.refreshing
}

/** True while an explicit refresh is re-reading either surface. */
export function isRefreshing(summary: SummaryView, branches: BranchesView): boolean {
  return refreshingOf(summary) || refreshingOf(branches)
}
/**
 * Whether the store holds data for the scope the URL names. Data of another TPIN
 * or period never reads as current. An unusable range arrives as empty strings, which no scope has.
 */
export function isCurrentScope(scope: TpinScope | null, tin: string, from: string, to: string): boolean {
  return scope !== null && scope.tin === tin && scope.from === from && scope.to === to
}

export type StatusKey = 'synced' | 'failed' | 'pending'

export const STATUS_KEYS: readonly StatusKey[] = ['synced', 'failed', 'pending']

export const STATUS_WORD: Readonly<Record<StatusKey, string>> = {
  synced: 'Synced',
  failed: 'Failed',
  pending: 'Pending',
}

/**
 * Where a KPI count drills (D-13/D-15): only a single-branch TPIN offers a
 * link, and only to the lists that exist (failed S3, pending S4). Synced has
 * no list, and a multi-branch TPIN drills through its branch rows instead.
 */
export function kpiTarget(key: StatusKey, shortcutBranch: string | null): string | null {
  if (shortcutBranch === null) return null
  if (key === 'failed') return failedListPath(shortcutBranch)
  if (key === 'pending') return pendingListPath(shortcutBranch)
  return null
}

/** One drillable KPI card: where it goes and the accessible name that states what it counts. */
export interface KpiLink {
  readonly to: string
  readonly ariaLabel: string
}

export type KpiLinks = Readonly<Record<StatusKey, KpiLink | null>>

/** No card drills (S-BRANCH: its counts are the branch itself, the worklists are the drill). */
export const NO_KPI_LINKS: KpiLinks = { synced: null, failed: null, pending: null }

/**
 * The links of a KPI row. A card that drills carries its own accessible name,
 * `{Status} — {count} invoices for {scope}`, so a reader hears what a count is
 * about rather than three disjoint numbers.
 */
export function kpiLinks(counts: Readonly<Record<StatusKey, number>>, shortcutBranch: string | null, scope: string): KpiLinks {
  const entries = STATUS_KEYS.map((key) => {
    const to = kpiTarget(key, shortcutBranch)
    const link: KpiLink | null = to === null ? null : { to, ariaLabel: `${STATUS_WORD[key]} — ${counts[key]} invoices for ${scope}` }
    return [key, link] as const
  })
  return Object.fromEntries(entries) as KpiLinks
}

/** Period restated under the KPI row; the created date owns the period (BR-3). */
export function scopeText(from: string, to: string): string {
  return from === to ? `Invoices created on ${from}` : `Invoices created between ${from} and ${to}`
}

/**
 * The distribution bar shows only the statuses that have invoices; the others
 * are named in the caption instead of drawn as empty segments. The bar uses the
 * server counts directly as flex weights - no percentage is computed.
 */
export function distributionOf(counts: Readonly<Record<StatusKey, number>>): {
  readonly present: readonly StatusKey[]
  readonly absent: readonly StatusKey[]
} {
  return {
    present: STATUS_KEYS.filter((key) => counts[key] > 0),
    absent: STATUS_KEYS.filter((key) => counts[key] === 0),
  }
}
const LOADING_SUMMARY: SummaryView = { kind: 'loading' }
const LOADING_BRANCHES: BranchesView = { kind: 'loading' }

/** The summary view for the scope the URL names; another scope's data reads as loading. */
export function summaryFor(
  state: { readonly scope: TpinScope | null; readonly summary: SummaryView },
  tin: string,
  from: string,
  to: string,
): SummaryView {
  return isCurrentScope(state.scope, tin, from, to) ? state.summary : LOADING_SUMMARY
}

/** The branch-list view for the scope the URL names; another scope's data reads as loading. */
export function branchesFor(
  state: { readonly scope: TpinScope | null; readonly branches: BranchesView },
  tin: string,
  from: string,
  to: string,
): BranchesView {
  return isCurrentScope(state.scope, tin, from, to) ? state.branches : LOADING_BRANCHES
}